// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trophy, Calendar } from "lucide-react";
import { parseLocalDate } from '@/utils/formatters';

interface LeaderboardData {
  id: string;
  name: string;
  description: string;
  sections: {
    id: string;
    name: string;
    display_order: number;
    sort_field_id?: string;
    sort_direction?: string;
    fields: {
      id: string;
      field_name: string;
      field_type: string;
      unit: string;
      display_order: number;
    }[];
    entries: {
      id: string;
      athlete_name: string;
      values: Record<string, string>;
    }[];
  }[];
  events?: {
    name: string;
    start_date: string;
  };
}

const PublicLeaderboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [leaderboard, setLeaderboard] = useState<LeaderboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchLeaderboard();
    }
  }, [id]);

  const fetchLeaderboard = async () => {
    try {
      // Fetch leaderboard with sections
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .from('leaderboards')
        .select(`
          *,
          events(name, start_date),
          leaderboard_sections(
            id,
            name,
            display_order,
            sort_field_id,
            sort_direction
          )
        `)
        .eq('id', id)
        .eq('is_published', true)
        .single();

      if (leaderboardError) {
        throw new Error('Leaderboard not found or not published');
      }

      // Fetch fields for each section separately
      const sectionsWithFields = await Promise.all(
        leaderboardData.leaderboard_sections.map(async (section: any) => {
          const { data: fieldsData, error: fieldsError } = await supabase
            .from('leaderboard_fields')
            .select('id, field_name, field_type, unit, display_order')
            .eq('section_id', section.id)
            .order('display_order');

          if (fieldsError) throw fieldsError;

          return {
            ...section,
            leaderboard_fields: fieldsData || []
          };
        })
      );

      if (leaderboardError) {
        throw new Error('Leaderboard not found or not published');
      }

      // Fetch entries with values for each section
      const sectionsWithEntries = await Promise.all(
        sectionsWithFields.map(async (section: any) => {
          const { data: entriesData, error: entriesError } = await supabase
            .from('leaderboard_entries')
            .select(`
              id,
              athlete_name
            `)
            .eq('leaderboard_id', id);

          if (entriesError) throw entriesError;

          // Fetch values for each entry separately to avoid relationship ambiguity
          const entriesWithValues = await Promise.all(
            (entriesData || []).map(async (entry: any) => {
              const { data: valuesData, error: valuesError } = await supabase
                .from('leaderboard_values')
                .select(`
                  field_id,
                  value,
                  leaderboard_fields!inner(id, field_name, section_id)
                `)
                .eq('entry_id', entry.id)
                .eq('leaderboard_fields.section_id', section.id);

              if (valuesError) throw valuesError;

              return {
                ...entry,
                leaderboard_values: valuesData || []
              };
            })
          );

          if (entriesError) throw entriesError;

          // Transform entries for this section
          const sectionEntries = entriesWithValues?.map(entry => ({
            id: entry.id,
            athlete_name: entry.athlete_name,
            values: entry.leaderboard_values.reduce((acc, val: any) => {
              acc[val.leaderboard_fields.field_name] = val.value;
              return acc;
            }, {} as Record<string, string>)
          })) || [];

          return {
            ...section,
            fields: section.leaderboard_fields.sort((a: any, b: any) => a.display_order - b.display_order),
            entries: sectionEntries
          };
        })
      );

      // Transform the data
      const transformedData: LeaderboardData = {
        ...leaderboardData,
        sections: sectionsWithEntries
          .sort((a, b) => a.display_order - b.display_order)
          .map(section => {
            // Sort entries for this section if sort configuration exists
            let sortedEntries = [...section.entries];
            
            if (section.sort_field_id && section.entries.length > 0) {
              // Find the field name for the sort field ID
              const sortField = section.fields.find(field => field.id === section.sort_field_id);
              const sortFieldName = sortField?.field_name;

              if (sortFieldName) {
                sortedEntries.sort((a, b) => {
                  const aValue = a.values[sortFieldName];
                  const bValue = b.values[sortFieldName];
                  
                  // Handle numeric sorting
                  const aNum = parseFloat(aValue);
                  const bNum = parseFloat(bValue);
                  
                  if (!isNaN(aNum) && !isNaN(bNum)) {
                    return section.sort_direction === 'asc' ? aNum - bNum : bNum - aNum;
                  }
                  
                  // Handle string sorting
                  if (section.sort_direction === 'asc') {
                    return (aValue || '').localeCompare(bValue || '');
                  } else {
                    return (bValue || '').localeCompare(aValue || '');
                  }
                });
              }
            }

            return {
              ...section,
              entries: sortedEntries
            };
          })
      };

      setLeaderboard(transformedData);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      setError(error instanceof Error ? error.message : 'Failed to load leaderboard');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold text-destructive mb-2">Leaderboard Not Found</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!leaderboard) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <h2 className="text-2xl font-bold mb-2">Leaderboard Not Found</h2>
        <p className="text-muted-foreground">This leaderboard may not exist or is not published.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Trophy className="h-6 w-6 text-primary" />
            <CardTitle className="text-3xl">{leaderboard.name}</CardTitle>
          </div>
          {leaderboard.description && (
            <p className="text-muted-foreground">{leaderboard.description}</p>
          )}
          {leaderboard.events && (
            <div className="flex items-center gap-2 mt-2">
              <Calendar className="h-4 w-4" />
              <Badge variant="outline">{leaderboard.events.name}</Badge>
              {leaderboard.events.start_date && (
                <span className="text-sm text-muted-foreground">
                  {parseLocalDate(leaderboard.events.start_date).toLocaleDateString()}
                </span>
              )}
            </div>
          )}
        </CardHeader>
      </Card>

      {leaderboard.sections.every(section => section.entries.length === 0) ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <h3 className="text-lg font-semibold mb-2">No Entries Yet</h3>
            <p className="text-muted-foreground">Results will appear here once data is entered.</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue={leaderboard.sections[0]?.id} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${leaderboard.sections.length}, minmax(0, 1fr))` }}>
            {leaderboard.sections.map((section) => (
              <TabsTrigger key={section.id} value={section.id} className="flex-1">
                {section.name}
              </TabsTrigger>
            ))}
          </TabsList>
          
          {leaderboard.sections.map((section) => (
            <TabsContent key={section.id} value={section.id} className="mt-6">
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{section.name}</CardTitle>
                    {section.sort_field_id && (
                      <Badge variant="outline">
                        Sorted by: {section.fields.find(f => f.id === section.sort_field_id)?.field_name} 
                        ({section.sort_direction === 'desc' ? 'High to Low' : 'Low to High'})
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {section.entries.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No entries for {section.name} yet.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-4 font-semibold">Rank</th>
                            {section.fields.map((field) => (
                              <th key={field.id} className="text-left py-2 px-4 font-semibold">
                                {field.field_name}
                                {field.unit && <span className="text-muted-foreground ml-1">({field.unit})</span>}
                                {field.id === section.sort_field_id && <span className="ml-1">⭐</span>}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {section.entries.map((entry, index) => (
                            <tr key={entry.id} className={index % 2 === 0 ? "bg-muted/50" : ""}>
                              <td className="py-2 px-4 font-medium">{index + 1}</td>
                              {section.fields.map((field) => (
                                <td key={field.id} className="py-2 px-4">
                                  {entry.values[field.field_name] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default PublicLeaderboard;